import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import QrReader from 'react-qr-reader';
import styledComponent from '../../utils/styledComponent';

const ScannerWrapper = styledComponent('div', {
  maxWidth: '320px',
  margin: '0 auto 20px',
  position: 'relative',
  '& section': {
    borderRadius: '8px',
    overflow: 'hidden',
  },
});

const FeedbackOverlay = styledComponent('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  color: '#fff',
  padding: '16px',
  textAlign: 'center',
  '& .glyphicon': {
    fontSize: '48px',
    marginBottom: '12px',
  },
  '& p': {
    fontSize: '18px',
    fontWeight: 700,
    margin: 0,
  },
});

const FEEDBACK_STYLES = {
  success: {
    background: 'rgba(54, 128, 7, 0.92)',
    icon: 'glyphicon-ok-circle',
  },
  error: {
    background: 'rgba(217, 40, 63, 0.92)',
    icon: 'glyphicon-remove-circle',
  },
  info: { background: 'rgba(42, 95, 165, 0.92)', icon: 'glyphicon-info-sign' },
};

// Users scan from mobile where toasts render off-screen; the result is
// shown over the camera view itself so it stays in their line of sight.
// Scanning is paused while it's visible, and the tabs reuse this value as
// the toast autoClose so only one message is ever on screen at a time.
export const FEEDBACK_DISPLAY_MS = 5000;

const CameraErrorBanner = styledComponent('div', {
  background: '#fff3cd',
  border: '1px solid #ff9e22',
  color: '#856404',
  padding: '12px 16px',
  borderRadius: '4px',
  marginBottom: '20px',
  fontSize: '14px',
});

// The camera decodes the same label on every pass while it stays in frame;
// ignore repeats within this window so one physical scan fires once.
const DUPLICATE_SCAN_COOLDOWN_MS = 2000;

const PalletQrScanner = ({ onScan, disabled }) => {
  const [cameraError, setCameraError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const lastScanRef = useRef({ code: null, at: 0 });
  const feedbackTimeoutRef = useRef(null);
  const isPausedRef = useRef(false);

  useEffect(() => () => clearTimeout(feedbackTimeoutRef.current), []);

  useEffect(() => {
    if (!disabled) {
      lastScanRef.current = { ...lastScanRef.current, at: Date.now() };
    }
  }, [disabled]);

  const handleScan = async (data) => {
    if (!data || disabled) return;
    // Ignore camera detections while a scan is being processed or its
    // result is still on screen, so rapid-fire scans can't stack messages.
    if (isPausedRef.current) return;
    const now = Date.now();
    if (
      data === lastScanRef.current.code &&
      now - lastScanRef.current.at < DUPLICATE_SCAN_COOLDOWN_MS
    ) {
      return;
    }
    lastScanRef.current = { code: data, at: now };
    isPausedRef.current = true;
    const result = await Promise.resolve(onScan(data)).catch((err) => {
      console.error('Scan handler error:', err);
      return null;
    });
    if (result && result.message) {
      setFeedback(result);
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        setFeedback(null);
        // Restart the duplicate window from dismissal so the same label
        // must leave the frame before it can trigger again.
        lastScanRef.current = { code: data, at: Date.now() };
        isPausedRef.current = false;
      }, FEEDBACK_DISPLAY_MS);
    } else {
      isPausedRef.current = false;
    }
  };

  const handleError = (err) => {
    console.error('QR scanner error:', err);
    setCameraError(
      'Check your device camera permissions, or enter the QR code manually below.',
    );
  };

  if (cameraError) {
    return (
      <CameraErrorBanner>
        <strong>Camera unavailable:</strong> {cameraError}
      </CameraErrorBanner>
    );
  }

  const feedbackStyle = feedback
    ? FEEDBACK_STYLES[feedback.status] || FEEDBACK_STYLES.info
    : null;

  return (
    <ScannerWrapper>
      <QrReader
        delay={300}
        onError={handleError}
        onScan={handleScan}
        facingMode="environment"
        style={{ width: '100%' }}
      />
      {feedback && (
        <FeedbackOverlay
          role="status"
          aria-live="polite"
          style={{ background: feedbackStyle.background }}
        >
          <span className={`glyphicon ${feedbackStyle.icon}`} />
          <p>{feedback.message}</p>
        </FeedbackOverlay>
      )}
    </ScannerWrapper>
  );
};

PalletQrScanner.propTypes = {
  onScan: PropTypes.func.isRequired,
  // Ignore scans entirely, e.g. while a confirmation modal is open.
  disabled: PropTypes.bool,
};

PalletQrScanner.defaultProps = {
  disabled: false,
};

export default PalletQrScanner;
